import { getFranchiseOrganizationId, readFranchiseFlowAuth } from '../../shared/franchiseFlowAuth';

export function isNyafazReelsFlow() {
  if (typeof window === 'undefined') return false;
  return (window.location?.pathname || '').includes('/nyafaz-reels');
}

/** Resolved per request — bundle may load on /naturals/presentation before nyafaz-reels navigation. */
export function getSalonVideoApiBase() {
  if (isNyafazReelsFlow()) {
    return '/nyafaz-reels-api';
  }
  const fromEnv = (process.env.REACT_APP_SALON_VIDEO_API_URL || '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const path = window.location?.pathname || '';
    if (path.includes('/naturals/')) {
      return '/naturals-salon-video-api';
    }
  }
  return '';
}

export function salonVideoApiPath() {
  if (isNyafazReelsFlow()) return '/api/nyafaz-reels';
  return '/api/salon-video';
}

function vpath(subpath) {
  const base = salonVideoApiPath();
  const tail = (subpath || '').startsWith('/') ? subpath : `/${subpath || ''}`;
  return `${base}${tail}`;
}

/** Naturals JSON2Video blueprint; Nyafaz Reels uses server-side FFmpeg (reel_renderer ignored on worker). */
function defaultReelRenderer() {
  if (isNyafazReelsFlow()) return 'ffmpeg';
  return (process.env.REACT_APP_SALON_VIDEO_REEL_RENDERER || 'alpha_blueprint').trim().toLowerCase();
}

/** Prefer franchise org id; fall back to env for local/dev only. */
export function resolveSalonId() {
  const fromFranchise = getFranchiseOrganizationId() || readFranchiseFlowAuth()?.organization_id;
  if (fromFranchise) return String(fromFranchise);
  return process.env.REACT_APP_SALON_VIDEO_SALON_ID || 'dev-salon';
}

/** Franchise tracking payload for analytics (org + franchise + stylist). */
export function resolveFranchiseTracking() {
  const auth = readFranchiseFlowAuth() || {};
  const organization_id = String(
    auth.organization_id || getFranchiseOrganizationId() || resolveSalonId() || ''
  ).trim();
  const franchise_id =
    auth.franchise_id != null && String(auth.franchise_id).trim()
      ? String(auth.franchise_id).trim()
      : null;
  const franchise_code =
    auth.franchise_code != null && String(auth.franchise_code).trim()
      ? String(auth.franchise_code).trim()
      : null;
  const stylist_id =
    auth.stylist_id || auth.login_account_id
      ? String(auth.stylist_id || auth.login_account_id).trim()
      : null;
  return { organization_id, franchise_id, franchise_code, stylist_id };
}

/** Prefer same-origin /api/salon-video/... so CRA proxy + <video> both work. */
export function resolveMediaUrl(url) {
  if (!url) return '';
  const apiBase = getSalonVideoApiBase();
  try {
    if (url.startsWith('/')) {
      return apiBase ? `${apiBase.replace(/\/$/, '')}${url}` : url;
    }
    const parsed = new URL(url, window.location.origin);
    let path = `${parsed.pathname}${parsed.search}`;
    if (path.includes('/naturals-salon-video-api/')) {
      path = path.replace(/^\/naturals-salon-video-api/, '');
    }
    if (path.includes('/nyafaz-reels-api/')) {
      path = path.replace(/^\/nyafaz-reels-api/, '');
    }
    if (path.startsWith('/api/salon-video/') || path.startsWith('/api/nyafaz-reels/')) {
      return apiBase ? `${apiBase.replace(/\/$/, '')}${path}` : path;
    }
  } catch {
    /* keep original */
  }
  return url;
}

async function request(path, options = {}) {
  const tracking = resolveFranchiseTracking();
  const headers = {
    'X-Salon-Id': resolveSalonId(),
    ...(tracking.organization_id ? { 'X-Organization-Id': tracking.organization_id } : {}),
    ...(tracking.franchise_id ? { 'X-Franchise-Id': tracking.franchise_id } : {}),
    ...(tracking.franchise_code ? { 'X-Franchise-Code': tracking.franchise_code } : {}),
    ...(tracking.stylist_id ? { 'X-Stylist-Id': tracking.stylist_id } : {}),
    ...(options.headers || {}),
  };
  if (options.json) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${getSalonVideoApiBase()}${path}`, {
    ...options,
    headers,
    body: options.json ? JSON.stringify(options.json) : options.body,
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const gateway = res.status === 502 || res.status === 503 || res.status === 504;
    const fallback = gateway
      ? 'Video service is unavailable. Please try again in a moment.'
      : 'Request failed';
    const err = new Error(data.message || data.error || fallback);
    err.payload = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  health: () => request(vpath('/health')),
  /** Upload a video; returns compressed MP4 blob (~65% size) plus size stats. */
  compressVideo: async (file) => {
    const MAX_BYTES = 2 * 1024 * 1024 * 1024; // keep in sync with backend/nginx
    if (Number(file?.size || 0) > MAX_BYTES) {
      const err = new Error('Video is too large. Maximum upload size is 2 GB.');
      err.status = 413;
      throw err;
    }
    const tracking = resolveFranchiseTracking();
    const form = new FormData();
    form.append('video', file, file.name || 'upload.mp4');
    const headers = {
      'X-Salon-Id': resolveSalonId(),
      ...(tracking.organization_id ? { 'X-Organization-Id': tracking.organization_id } : {}),
      ...(tracking.franchise_id ? { 'X-Franchise-Id': tracking.franchise_id } : {}),
      ...(tracking.franchise_code ? { 'X-Franchise-Code': tracking.franchise_code } : {}),
      ...(tracking.stylist_id ? { 'X-Stylist-Id': tracking.stylist_id } : {}),
    };
    const res = await fetch(`${getSalonVideoApiBase()}${vpath('/compress')}`, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'include',
    });
    if (!res.ok) {
      if (res.status === 413) {
        const err = new Error('Video is too large for the server (max 2 GB). Try a shorter clip.');
        err.status = 413;
        throw err;
      }
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.message || data.error || 'Compression failed');
      err.payload = data;
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const originalSize = Number(res.headers.get('X-Original-Size') || file.size || 0);
    const compressedSize = Number(res.headers.get('X-Compressed-Size') || blob.size || 0);
    const achievedRatio = Number(res.headers.get('X-Achieved-Ratio') || 0);
    const targetRatio = Number(res.headers.get('X-Target-Ratio') || 0.65);
    const note = res.headers.get('X-Compress-Note') || 'ok';
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `${(file.name || 'video').replace(/\.[^.]+$/, '')}-65pct.mp4`;
    const objectUrl = URL.createObjectURL(blob);
    return {
      blob,
      objectUrl,
      filename,
      originalSize,
      compressedSize,
      achievedRatio,
      targetRatio,
      note,
    };
  },
  musicOptions: () => request(vpath('/music-options')),
  listSessions: (limit = 50) => request(`${vpath('/sessions')}?limit=${limit}`),
  createSession: (serviceType, style = 'premium', extras = {}) => {
    const auth = readFranchiseFlowAuth() || {};
    const orgLogo = extras.org_logo_url || auth.org_logo || '';
    const orgName = extras.org_name || auth.org_name || '';
    return request(vpath('/session'), {
      method: 'POST',
      json: {
        service_type: serviceType,
        style,
        franchise_name: extras.franchise_name || auth.franchise_name,
        ...(orgLogo ? { org_logo_url: orgLogo } : {}),
        ...(orgName ? { org_name: orgName } : {}),
        ...(extras.music_mood ? { music_mood: extras.music_mood } : {}),
        ...(extras.music_track ? { music_track: extras.music_track } : {}),
        ...(extras.reel_text_color ? { reel_text_color: extras.reel_text_color } : {}),
        ...(extras.checkin_id ? { checkin_id: extras.checkin_id } : {}),
        reel_renderer: extras.reel_renderer || defaultReelRenderer(),
        engine: isNyafazReelsFlow() ? 'nyafaz_reels' : undefined,
        ...resolveFranchiseTracking(),
      },
    });
  },
  getSession: (id) => request(vpath(`/${id}`)),
  getDownloadLink: (id) => request(vpath(`/${id}/download`)),
  deleteSession: (id) => request(vpath(`/${id}`), { method: 'DELETE' }),
  saveShare: (id, payload) => request(vpath(`/${id}/share`), { method: 'POST', json: payload }),
  uploadClip: async (id, clipType, blob, sequence = 0) => {
    const mime = String(blob?.type || '').toLowerCase();
    const ext = mime.includes('mp4') || mime.includes('quicktime') ? 'mp4' : 'webm';
    const filename = `${clipType}-${sequence}.${ext}`;
    const contentType = mime.includes('mp4') || mime.includes('quicktime')
      ? 'video/mp4'
      : mime.includes('webm')
        ? 'video/webm'
        : blob?.type || 'video/mp4';
    const byteSize = Number(blob?.size || 0);

    const uploadViaProxy = async () => {
      const form = new FormData();
      form.append('file', blob, filename);
      form.append('clip_type', clipType);
      form.append('sequence', String(sequence));
      return request(vpath(`/${id}/clip`), { method: 'POST', body: form });
    };

    // Prefer browser → object storage PUT (avoids slow phone→API hop).
    try {
      const t0 = performance.now();
      const presign = await request(vpath(`/${id}/clip/presign`), {
        method: 'POST',
        json: {
          clip_type: clipType,
          sequence,
          content_type: contentType,
          byte_size: byteSize,
          filename,
        },
      });
      const t1 = performance.now();
      if (!presign?.upload_url || !presign?.object_key) {
        throw new Error('Presign response missing upload_url');
      }
      const putHeaders = {
        'Content-Type': presign.content_type || contentType,
        ...(presign.headers || {}),
      };
      const putRes = await fetch(presign.upload_url, {
        method: 'PUT',
        headers: putHeaders,
        body: blob,
      });
      const t2 = performance.now();
      if (!putRes.ok) {
        const text = await putRes.text().catch(() => '');
        throw new Error(`Direct upload failed (${putRes.status}) ${text.slice(0, 120)}`);
      }
      const completed = await request(vpath(`/${id}/clip/complete`), {
        method: 'POST',
        json: {
          clip_type: clipType,
          sequence,
          object_key: presign.object_key,
          content_type: contentType,
          byte_size: byteSize,
          filename,
        },
      });
      const t3 = performance.now();
      if (typeof console !== 'undefined' && console.info) {
        console.info(
          '[reels] direct upload',
          clipType,
          `bytes=${byteSize}`,
          `presign_ms=${Math.round(t1 - t0)}`,
          `put_ms=${Math.round(t2 - t1)}`,
          `complete_ms=${Math.round(t3 - t2)}`,
          `total_ms=${Math.round(t3 - t0)}`
        );
      }
      return completed;
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[reels] direct upload failed, falling back to API proxy', err);
      }
      return uploadViaProxy();
    }
  },
  validate: (id) => request(vpath(`/${id}/validate`), { method: 'POST' }),
  render: (id, extras = {}) =>
    request(vpath(`/${id}/render`), {
      method: 'POST',
      json: { reel_renderer: extras.reel_renderer || defaultReelRenderer() },
    }),
  approve: (id, whatsappConsent, instagramConsent, extras = {}) =>
    request(vpath(`/${id}/approve`), {
      method: 'POST',
      json: {
        whatsapp_consent: whatsappConsent,
        instagram_consent: instagramConsent,
        whatsapp_id: extras.whatsapp_id,
        instagram_id: extras.instagram_id,
      },
    }),
};
